import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  Divider,
  Button,
  CircularProgress,
  LinearProgress,
  Grid,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  DialogActions,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  CalendarMonth,
  ArrowBack,
  Timer,
  Euro,
  OpenInNew,
  PersonAdd,
  Bolt,
  Verified,
  Info,
  ContactSupport,
  LocationOn,
  PeopleAlt,
  CheckCircle,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  VideoCall as VideoIcon,
  EventNote as EventIcon,
  Person as PersonIcon,
  VerifiedUser as VerifiedIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import axios from 'axios';
import './WorkshopDetails.css';
import AddParticipantModal from './AddParticipantModal';
import { useAuth } from '../../contexts/AuthContext';
import ChatAtelier from './ChatAtelier';
import { toast } from 'react-toastify';
import DescriptionIcon from '@mui/icons-material/Description';
import BoltIcon from '@mui/icons-material/Bolt';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
const WorkshopDetails = () => {
  const { user } = useAuth();

  const { id } = useParams();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const rolePath = user?.role === 3 ? 'intervenant' : 'admin';
  const isIntervenant = user?.role === 3;
  const [selectedClient, setSelectedClient] = useState(null);
  const handleClose = () => setOpen(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedContext, setSelectedContext] = useState(null);

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [files, setFiles] = useState([]);
  const handleFilesChange = (e) => {
    setFiles(Array.from(e.target.files));
  };
  const handleOpenReportDialog = (session) => {
    setSelectedSession(session);
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setSelectedSession(null);
    setReportDialogOpen(false);
  };

  const handleViewReport = (session) => {
    setSelectedSession(session);
    setSelectedReport(session.report);
    setReportDialogOpen(true);
  };

  // MODIFIEZ CECI DANS VOTRE USESTATE INITIAL
  const [form, setForm] = useState({
    content: '',
    summary: '',
    key_points: [],
    decisions: [],
    action_items: [],
    tags: [],
  });
  useEffect(() => {
    if (selectedSession && !selectedReport) {
      setForm({
        content: '',
        summary: '',
        key_points: [],
        decisions: [],
        action_items: [],
        tags: [],
      });
    }
  }, [selectedSession, selectedReport]);
  const getApiErrorMessage = (err) => {
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      Object.values(err?.response?.data?.errors || {})
        ?.flat()
        ?.join(', ') ||
      'Une erreur est survenue'
    );
  };
  const handleSubmitReport = async () => {
    try {
      const formData = new FormData();

      // =========================
      // INFOS PRINCIPALES
      // =========================
      formData.append('id_wss', selectedSession?.id_wss);
      formData.append('id_intervenant_ws', user?.id);

      formData.append('content', form.content || '');
      formData.append('summary', form.summary || '');

      // =========================
      // ARRAYS (IMPORTANT JSON STRINGIFY)
      // =========================
      formData.append('key_points', JSON.stringify(form.key_points || []));
      formData.append('decisions', JSON.stringify(form.decisions || []));
      formData.append('action_items', JSON.stringify(form.action_items || []));
      formData.append('tags', JSON.stringify(form.tags || []));

      // =========================
      // FILES (UPLOAD MULTIPLE)
      // =========================
      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append('files[]', file); // clé importante
        });
      }

      // =========================
      // API CALL
      // =========================
      await axios.post(
        `${process.env.REACT_APP_API_URL}/${rolePath}/v1/workshop-reports/store`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            Accept: 'application/json',
            // ⚠️ ne force pas boundary, axios le fait automatiquement
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success('Rapport créé avec succès ✅');

      await fetchDetails();

      // reset state
      setReportDialogOpen(false);
      setSelectedSession(null);
      setSelectedReport(null);
      setFiles([]);

      setForm({
        content: '',
        summary: '',
        key_points: [],
        decisions: [],
        action_items: [],
        tags: [],
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const safeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return val ? [val] : [];
      }
    }
    return [];
  };
  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    // Reset l'icône après 2 secondes
    setTimeout(() => setCopied(false), 2000);
  };

  const [open, setOpen] = useState(false);
  const fetchDetails = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/${rolePath}/atelier/g/l/list`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      const found = res.data.result.find((item) => item.id_ws === parseInt(id));
      setWorkshop(found);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);
  // Total global des inscrits
  const allParticipantsCount =
    workshop?.sessions?.reduce(
      (acc, s) => acc + (s.participants?.length || 0),
      0
    ) || 0;

  const handleShowClientDetails = ({ session, participant }) => {
    setSelectedContext({ session, participant });
    setOpen(true);
  };

  useEffect(() => {}, [selectedContext]);

  const generateReportPDF = (session, report) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();

    // =========================
    // HEADER
    // =========================
    doc.setFillColor(67, 24, 255);
    doc.rect(0, 0, pageWidth, 35, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Compte-rendu de session', 14, 18);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    doc.text(
      `${workshop?.title_ws || 'Atelier'} - ${
        session?.date_wss
          ? new Date(session.date_wss).toLocaleDateString('fr-FR')
          : ''
      }`,
      14,
      27
    );

    let y = 48;

    // =========================
    // INFOS SESSION
    // =========================
    doc.setTextColor(27, 37, 89);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Informations session', 14, y);

    y += 8;

    autoTable(doc, {
      startY: y,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [67, 24, 255],
      },
      body: [
        ['Formation', workshop?.title_ws || '-'],
        [
          'Date session',
          session?.date_wss
            ? new Date(session.date_wss).toLocaleString('fr-FR')
            : '-',
        ],
        [
          'Début session',
          session?.session_started_at
            ? new Date(session.session_started_at).toLocaleString('fr-FR')
            : '-',
        ],
        [
          'Fin session',
          session?.session_ended_at
            ? new Date(session.session_ended_at).toLocaleString('fr-FR')
            : '-',
        ],

        ['Durée formation', `${workshop?.duration_ws || 0} min`],
        ['Format', workshop?.format_ws || '-'],

        [
          'Intervenant',
          `${workshop?.intervenant?.prenom_user || ''} ${workshop?.intervenant?.nom_user || ''}`,
        ],

        // =========================
        // STATS BUSINESS
        // =========================
        [
          'Participants inscrits',
          session?.participants_count ?? session?.participants?.length ?? 0,
        ],
        [
          'Taux de présence',
          session?.attendance_rate ? `${session.attendance_rate}%` : '-',
        ],
        [
          'Note session',
          session?.session_rating ? `${session.session_rating}/5` : '-',
        ],

        // =========================
        // FEEDBACK
        // =========================
        ['Feedback', session?.feedback_notes || '-'],
      ],
    });

    y = doc.lastAutoTable.finalY + 12;

    // =========================
    // RÉSUMÉ
    // =========================
    if (report?.summary) {
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('Résumé', 14, y);

      y += 8;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      const summaryLines = doc.splitTextToSize(report.summary, 180);

      doc.text(summaryLines, 14, y);

      y += summaryLines.length * 6 + 10;
    }

    // =========================
    // CONTENU
    // =========================
    if (report?.content) {
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('Contenu détaillé', 14, y);

      y += 8;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      const contentLines = doc.splitTextToSize(report.content, 180);

      doc.text(contentLines, 14, y);

      y += contentLines.length * 6 + 10;
    }

    // =========================
    // TABLEAUX
    // =========================
    const buildSection = (title, data) => {
      if (!data || data.length === 0) return;

      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, y);

      y += 5;

      autoTable(doc, {
        startY: y,
        theme: 'striped',
        head: [['Élément']],
        body: data.map((item) => [item]),
        headStyles: {
          fillColor: [5, 205, 153],
        },
        styles: {
          fontSize: 10,
        },
      });

      y = doc.lastAutoTable.finalY + 10;
    };

    buildSection('Points clés', report?.key_points);
    buildSection('Décisions', report?.decisions);
    buildSection('Actions', report?.action_items);
    buildSection('Tags', report?.tags);

    // =========================
    // PARTICIPANTS
    // =========================
    if (session?.participants?.length > 0) {
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('Participants', 14, y);

      y += 5;

      autoTable(doc, {
        startY: y,
        head: [['Nom', 'Email']],
        body: session.participants.map((p) => [
          `${p?.client?.prenom_client || ''} ${p?.client?.nom_client || ''}`,
          p?.client?.email_client || '-',
        ]),
        headStyles: {
          fillColor: [67, 24, 255],
        },
        styles: {
          fontSize: 10,
        },
      });

      y = doc.lastAutoTable.finalY + 10;
    }

    // =========================
    // FOOTER
    // =========================
    const pageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      doc.setFontSize(9);
      doc.setTextColor(120);

      doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, 14, 290);

      doc.text(`Page ${i}/${pageCount}`, 180, 290);
    }

    // =========================
    // SAVE
    // =========================
    doc.save(`rapport-session-${session?.id_wss || 'workshop'}.pdf`);
  };

  const handleDownloadFile = async (file) => {
    const fileUrl = `${process.env.REACT_APP_API_URL}${file.url}`;

    setDownloading(true);

    try {
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error('Erreur téléchargement');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = file.name || 'file';

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);

      // fallback propre
      window.open(fileUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !workshop) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#4318FF' }} />
      </Box>
    );
  }

  if (!workshop) return <Typography>Atelier introuvable.</Typography>;

  return (
    <div className="page-wrapper">
      {/* HEADER DYNAMIQUE */}
      <div className="header-blue">
        <div className="content-container">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ArrowBack />}
              sx={{ color: '#fff', textTransform: 'none' }}
            >
              Retour au Dashboard
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenAddModal(true)}
              startIcon={<PersonAdd />}
              sx={{
                bgcolor: '#4318FF',
                borderRadius: '12px',
                fontWeight: '700',
                textTransform: 'none',
                px: 3,
              }}
            >
              Nouvelle Inscription
            </Button>
          </Stack>

          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              <Chip
                label="Session Active"
                size="small"
                sx={{ bgcolor: '#05CD99', color: '#fff', fontWeight: '800' }}
              />
              <Chip
                label={workshop.format_ws}
                size="small"
                sx={{
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.4)',
                  bgcolor: 'transparent',
                }}
              />
            </Stack>
            <Typography
              variant="h1"
              className="workshop-title"
              sx={{ color: 'white', fontWeight: 850, letterSpacing: '-1px' }}
            >
              {workshop.title_ws}
            </Typography>
          </Stack>
        </div>
      </div>

      {/* CORPS DE PAGE */}
      <div className="content-container">
        <div className="main-grid">
          {/* COLONNE GAUCHE */}
          <div className="col-main">
            {/* STATS RAPIDES */}
            <div className="card-white">
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={6} md={3}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontWeight: '700' }}
                  >
                    DURÉE
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: '800',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Timer color="primary" fontSize="small" />{' '}
                    {workshop.duration_ws} min
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontWeight: '700' }}
                  >
                    TARIF UNITAIRE
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: '800',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Euro sx={{ color: '#05CD99' }} fontSize="small" />{' '}
                    {workshop.price_ws} €
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontWeight: '700' }}
                  >
                    REMPLISSAGE GLOBAL
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    mt={0.5}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={
                        (allParticipantsCount / workshop.capacity_ws) * 100
                      }
                      sx={{
                        flexGrow: 1,
                        height: 10,
                        borderRadius: 5,
                        bgcolor: '#F4F7FE',
                      }}
                    />
                    <Typography sx={{ fontWeight: '800' }}>
                      {allParticipantsCount}/{workshop.capacity_ws}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </div>

            {/* DESCRIPTION DÉTAILLÉE */}
            <div className="card-white" style={{ padding: '32px' }}>
              {/* En-tête de section */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  color: '#1B2559', // Bleu très foncé typique SaaS
                }}
              >
                <Info sx={{ color: '#4318FF', fontSize: '1.75rem' }} />
                Détails de la formation
              </Typography>

              {/* Description avec meilleur espacement */}
              <Typography
                sx={{
                  color: '#475467', // Gris plus moderne (Inter/Linear style)
                  lineHeight: 1.8,
                  fontSize: '1.05rem',
                  whiteSpace: 'pre-line',
                  mb: 4,
                }}
              >
                {workshop.description_ws}
              </Typography>

              <Divider sx={{ my: 4, borderColor: '#F4F7FE' }} />

              {/* Grid des points clés */}
              <Grid
                container
                spacing={3}
                sx={{ mb: workshop.format_ws?.includes('presentiel') ? 4 : 0 }}
              >
                {[
                  {
                    icon: <Bolt sx={{ color: '#FFB547' }} />,
                    text: 'Accès immédiat',
                  },
                  {
                    icon: <Verified sx={{ color: '#05CD99' }} />,
                    text: 'Support inclus',
                  },
                  {
                    icon: <LocationOn sx={{ color: '#4318FF' }} />,
                    text: workshop.format_ws,
                  },
                ].map((item, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: '12px',
                        transition: '0.3s',
                        '&:hover': { bgcolor: '#F8FAFC' },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          p: 1,
                          borderRadius: '10px',
                          bgcolor: 'rgba(244, 247, 254, 0.7)',
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: '#2B3674',
                          fontSize: '0.95rem',
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Section Lieu (Si présentiel) */}
              {workshop.format_ws?.includes('presentiel') &&
                (workshop.address_ws || workshop.city_ws) && (
                  <Box
                    sx={{
                      mt: 4,
                      p: 3,
                      borderRadius: '20px',
                      bgcolor: '#F4F7FE', // Fond léger pour délimiter la zone
                      border: '1px solid #E0E5F2',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: '#4318FF',
                      }}
                    >
                      <LocationOn fontSize="small" /> Lieu de l’atelier
                    </Typography>

                    <Grid container spacing={3} alignItems="flex-start">
                      {/* Rue / Adresse */}
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#A3AED0',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          Adresse
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1B2559' }}>
                          {workshop.address_ws || 'N/A'}
                        </Typography>
                      </Grid>

                      {/* Ville & CP */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#A3AED0',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          Ville & CP
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1B2559' }}>
                          {workshop.city_ws}{' '}
                          {workshop.postal_code_ws
                            ? `(${workshop.postal_code_ws})`
                            : ''}
                        </Typography>
                      </Grid>

                      {/* Pays */}
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#A3AED0',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          Pays
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1B2559' }}>
                          {workshop.country_ws || 'France'}
                        </Typography>
                      </Grid>

                      {/* Coordonnées GPS (Badget style) */}
                      {workshop.lat_ws && (
                        <Grid
                          item
                          xs={12}
                          md={3}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Box
                            sx={{
                              p: 1,
                              bgcolor: '#FFF',
                              borderRadius: '10px',
                              border: '1px solid #E0E5F2',
                              textAlign: 'center',
                              width: '100%',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: '#A3AED0', display: 'block' }}
                            >
                              Coordonnées GPS
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#2B3674',
                              }}
                            >
                              {workshop.lat_ws}, {workshop.lng_ws}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
            </div>

            {/* SESSIONS ET PARTICIPANTS PAR SESSION */}
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, ml: 1 }}>
              Calendrier & Inscriptions
            </Typography>

            {workshop.sessions?.map((session) => {
              // Détection si la session est AUJOURD'HUI
              const sessionDate = new Date(session.date_wss).toDateString();
              const today = new Date().toDateString();
              const isToday = sessionDate === today;

              return (
                <div
                  key={session.id_wss}
                  className={`session-card ${isToday ? 'is-today' : ''}`}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Stack direction="row" spacing={3}>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: isToday ? '#05CD99' : '#fff', // Icône verte si c'est aujourd'hui
                          borderRadius: '15px',
                          color: isToday ? '#fff' : '#4318FF',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                        }}
                      >
                        <CalendarMonth fontSize="large" />
                      </Box>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {isToday && <span className="live-indicator"></span>}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color: isToday ? '#05CD99' : '#1B254B',
                            }}
                          >
                            {new Date(session.date_wss).toLocaleDateString(
                              'fr-FR',
                              { weekday: 'long', day: 'numeric', month: 'long' }
                            )}
                          </Typography>
                          {isToday && (
                            <Chip
                              label="EN DIRECT AUJOURD'HUI"
                              size="small"
                              sx={{
                                bgcolor: '#05CD99',
                                color: '#fff',
                                fontWeight: '900',
                                fontSize: '10px',
                              }}
                            />
                          )}
                        </Stack>
                        <Typography
                          variant="body2"
                          sx={{ color: '#A3AED0', fontWeight: '600' }}
                        >
                          Début :{' '}
                          {new Date(session.date_wss).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Box>
                    </Stack>

                    <Button
                      variant="contained"
                      href={session.intervenant_join_url_wss}
                      target="_blank"
                      endIcon={<OpenInNew />}
                      sx={{
                        bgcolor: isToday ? '#05CD99' : '#1B254B', // Bouton vert si c'est aujourd'hui
                        '&:hover': { bgcolor: isToday ? '#04b386' : '#000' },
                        borderRadius: '12px',
                        textTransform: 'none',
                        px: 3,
                      }}
                    >
                      {isToday
                        ? 'Lancer la session maintenant'
                        : 'Lien de réunion'}
                    </Button>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      {/* Icône rapport existant */}
                      {session.report ? (
                        <Box sx={{ mb: 2 }}>
                          {/* Titre et Flash */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mb: 1 }}
                          >
                            <BoltIcon
                              sx={{ color: '#f59e0b', fontSize: '1.2rem' }}
                            />{' '}
                            {/* Icône Flash */}
                            <Typography
                              variant="overline"
                              sx={{
                                fontWeight: 800,
                                color: '#475569',
                                letterSpacing: '0.05em',
                              }}
                            >
                              Compte-rendu de session
                            </Typography>
                          </Stack>

                          {/* Bloc de contrôle existant */}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{
                              backgroundColor: '#ffffff',
                              p: 1.5,
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            }}
                          >
                            <Chip
                              label="Disponible"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                border: 'none',
                                borderRadius: '8px',
                              }}
                            />

                            <Typography
                              variant="body2"
                              sx={{ color: '#64748b', fontWeight: 500 }}
                            >
                              {new Date(
                                session.report.created_at
                              ).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </Typography>

                            <Box sx={{ flexGrow: 1 }} />

                            <Stack direction="row" spacing={1}>
                              {/* Voir rapport */}
                              <IconButton
                                onClick={() => handleViewReport(session)}
                                sx={{
                                  color: '#4f46e5',
                                  backgroundColor: '#eef2ff',
                                  '&:hover': {
                                    backgroundColor: '#e0e7ff',
                                  },
                                }}
                              >
                                <DescriptionIcon fontSize="small" />
                              </IconButton>

                              {/* Télécharger PDF */}
                              <IconButton
                                onClick={() =>
                                  generateReportPDF(session, session.report)
                                }
                                sx={{
                                  color: '#dc2626',
                                  backgroundColor: '#fef2f2',
                                  '&:hover': {
                                    backgroundColor: '#fee2e2',
                                  },
                                }}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenReportDialog(session)}
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          + Compte rendu
                        </Button>
                      )}
                    </Stack>
                    <Dialog
                      open={reportDialogOpen}
                      onClose={handleCloseReportDialog}
                      fullWidth
                      maxWidth="md"
                      PaperProps={{
                        sx: { borderRadius: 4, overflow: 'hidden' },
                      }}
                    >
                      <DialogTitle
                        sx={{
                          px: 4,
                          pt: 3,
                          pb: 2,
                          fontWeight: 800,
                          fontSize: '1.4rem',
                        }}
                      >
                        {selectedReport
                          ? 'Consultation du rapport'
                          : 'Nouveau compte-rendu'}
                      </DialogTitle>

                      <DialogContent
                        dividers
                        sx={{ px: 4, py: 4, backgroundColor: '#fcfcfd' }}
                      >
                        <Stack spacing={3}>
                          {/* SECTION RÉSUMÉ & CONTENU */}
                          <TextField
                            label="Résumé"
                            fullWidth
                            variant="outlined" // Plus moderne que "filled"
                            value={
                              selectedReport
                                ? selectedReport.summary
                                : form.summary
                            }
                            InputProps={{
                              readOnly: !!selectedReport,
                              sx: { borderRadius: 2, backgroundColor: '#fff' },
                            }}
                            onChange={(e) =>
                              setForm({ ...form, summary: e.target.value })
                            }
                          />

                          <TextField
                            label="Contenu détaillé"
                            fullWidth
                            multiline
                            rows={6}
                            variant="outlined"
                            value={
                              selectedReport
                                ? selectedReport.content
                                : form.content
                            }
                            InputProps={{
                              readOnly: !!selectedReport,
                              sx: { borderRadius: 2, backgroundColor: '#fff' },
                            }}
                            onChange={(e) =>
                              setForm({ ...form, content: e.target.value })
                            }
                          />

                          {/* SECTION DONNÉES STRUCTUREES */}
                          {/* On utilise une grille plus propre pour aligner les champs par paires */}
                          <Stack direction="row" spacing={2}>
                            <TextField
                              fullWidth
                              label="Points clés"
                              placeholder="Un point par ligne"
                              variant="outlined"
                              multiline
                              minRows={4}
                              value={
                                selectedReport
                                  ? (selectedReport.key_points || []).join('\n')
                                  : form.key_points.join('\n')
                              }
                              InputProps={{
                                readOnly: !!selectedReport,
                                sx: {
                                  borderRadius: 2,
                                  backgroundColor: '#fff',
                                },
                              }}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  key_points: e.target.value
                                    .split('\n')
                                    .map((x) => x.trim())
                                    .filter(Boolean),
                                })
                              }
                            />
                            <TextField
                              fullWidth
                              label="Décisions"
                              placeholder="Une décision par ligne"
                              variant="outlined"
                              multiline
                              minRows={4}
                              value={
                                selectedReport
                                  ? (selectedReport.decisions || []).join('\n')
                                  : form.decisions.join('\n')
                              }
                              InputProps={{
                                readOnly: !!selectedReport,
                                sx: {
                                  borderRadius: 2,
                                  backgroundColor: '#fff',
                                },
                              }}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  decisions: e.target.value
                                    .split('\n')
                                    .map((x) => x.trim())
                                    .filter(Boolean),
                                })
                              }
                            />
                          </Stack>

                          <Stack direction="row" spacing={2}>
                            <TextField
                              fullWidth
                              label="Actions"
                              placeholder="Une action par ligne"
                              variant="outlined"
                              multiline
                              minRows={4}
                              value={
                                selectedReport
                                  ? (selectedReport.action_items || []).join(
                                      '\n'
                                    )
                                  : form.action_items.join('\n')
                              }
                              InputProps={{
                                readOnly: !!selectedReport,
                                sx: {
                                  borderRadius: 2,
                                  backgroundColor: '#fff',
                                },
                              }}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  action_items: e.target.value
                                    .split('\n')
                                    .map((x) => x.trim())
                                    .filter(Boolean),
                                })
                              }
                            />
                            <TextField
                              fullWidth
                              label="Tags"
                              placeholder="Entrez les tags"
                              variant="outlined"
                              value={
                                selectedReport
                                  ? (selectedReport.tags || []).join(', ')
                                  : form.tags.join(', ')
                              }
                              InputProps={{
                                readOnly: !!selectedReport,
                                sx: {
                                  borderRadius: 2,
                                  backgroundColor: '#fff',
                                },
                              }}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  tags: e.target.value
                                    .split(',')
                                    .map((x) => x.trim())
                                    .filter(Boolean),
                                })
                              }
                            />
                          </Stack>
                          <Stack direction="row" spacing={2}>
                            <Button
                              variant="outlined"
                              component="label"
                              sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                              Upload fichiers
                              <input
                                type="file"
                                hidden
                                multiple
                                onChange={handleFilesChange}
                              />
                            </Button>
                            {files.length > 0 && (
                              <Stack spacing={1} sx={{ mt: 1 }}>
                                {files.map((file, i) => (
                                  <Chip
                                    key={i}
                                    label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                                    onDelete={() =>
                                      setFiles(
                                        files.filter((_, index) => index !== i)
                                      )
                                    }
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                          {/* FILES (MODE CONSULTATION) */}
                          {selectedReport?.files?.length > 0 && (
                            <Box sx={{ pt: 2 }}>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                              >
                                {selectedReport?.files?.length > 0 && (
                                  <Box sx={{ pt: 2 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 800,
                                        mb: 2,
                                        color: '#1B2559',
                                      }}
                                    >
                                      Fichiers joints
                                    </Typography>

                                    <Stack spacing={2}>
                                      {selectedReport.files.map((file, i) => {
                                        const fileUrl = `${process.env.REACT_APP_API_URL}${file.url}`;
                                        const isImage =
                                          file.type?.startsWith('image/');

                                        return (
                                          <Box
                                            key={i}
                                            sx={{
                                              border: '1px solid #E2E8F0',
                                              borderRadius: 3,
                                              overflow: 'hidden',
                                              backgroundColor: '#fff',
                                            }}
                                          >
                                            {/* PREVIEW IMAGE */}
                                            {isImage && (
                                              <Box
                                                component="img"
                                                src={fileUrl}
                                                alt={file.name}
                                                sx={{
                                                  width: '100%',
                                                  maxHeight: 250,
                                                  objectFit: 'cover',
                                                  display: 'block',
                                                  backgroundColor: '#F8FAFC',
                                                }}
                                              />
                                            )}

                                            {/* FOOTER ACTION */}
                                            <Stack
                                              direction="row"
                                              alignItems="center"
                                              spacing={2}
                                              sx={{ p: 2 }}
                                            >
                                              <DescriptionIcon
                                                sx={{ color: '#4318FF' }}
                                              />

                                              <Box sx={{ flexGrow: 1 }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 700,
                                                    color: '#1B2559',
                                                  }}
                                                >
                                                  {file.name}
                                                </Typography>

                                                <Typography
                                                  variant="caption"
                                                  sx={{ color: '#64748B' }}
                                                >
                                                  {(file.size / 1024).toFixed(
                                                    1
                                                  )}{' '}
                                                  KB
                                                </Typography>
                                              </Box>

                                              {/* OUVRIR */}
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                href={fileUrl}
                                                target="_blank"
                                                sx={{
                                                  textTransform: 'none',
                                                  borderRadius: 2,
                                                }}
                                              >
                                                Ouvrir
                                              </Button>

                                              {/* GESTION FICHIERS */}
                                              <Button
                                                size="small"
                                                variant="contained"
                                                disabled={downloading}
                                                onClick={() =>
                                                  handleDownloadFile(file)
                                                }
                                                sx={{
                                                  textTransform: 'none',
                                                  borderRadius: 2,
                                                  bgcolor: '#4318FF',
                                                  '&:hover': {
                                                    bgcolor: '#3311CC',
                                                  },
                                                }}
                                              >
                                                {downloading
                                                  ? 'Téléchargement...'
                                                  : 'Télécharger'}
                                              </Button>
                                            </Stack>
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </DialogContent>

                      <DialogActions sx={{ p: 3, gap: 1 }}>
                        <Button
                          onClick={handleCloseReportDialog}
                          color="inherit"
                          sx={{ textTransform: 'none', px: 3 }}
                        >
                          Fermer
                        </Button>
                        {!selectedReport && (
                          <Button
                            variant="contained"
                            onClick={handleSubmitReport}
                            sx={{
                              textTransform: 'none',
                              px: 4,
                              borderRadius: 2,
                              boxShadow: 'none',
                            }}
                          >
                            Enregistrer le rapport
                          </Button>
                        )}
                      </DialogActions>
                    </Dialog>
                  </Box>

                  {/* Liste des participants reste la même */}
                  <div className="participant-mini-list">
                    <Typography
                      variant="caption"
                      sx={{
                        width: '100%',
                        color: '#A3AED0',
                        fontWeight: '800',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PeopleAlt fontSize="inherit" /> PARTICIPANTS INSCRITS (
                      {session.participants?.length || 0})
                    </Typography>

                    {session.participants?.map((p, idx) => (
                      <Tooltip
                        key={idx}
                        title={!isIntervenant ? p.client?.email_client : ''}
                      >
                        <div
                          className="participant-tag"
                          onClick={() =>
                            handleShowClientDetails({
                              session,
                              participant: p,
                            })
                          }
                          style={{ cursor: 'pointer' }} // Pour montrer que c'est cliquable
                        >
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              fontSize: '10px',
                              bgcolor: isToday ? '#05CD99' : '#4318FF',
                            }}
                          >
                            {p.client?.prenom_client?.charAt(0)}
                          </Avatar>

                          <Typography
                            variant="caption"
                            sx={{ fontWeight: '700' }}
                          >
                            {isIntervenant
                              ? p.client?.prenom_client
                              : `${p.client?.prenom_client} ${p.client?.nom_client}`}
                          </Typography>
                        </div>
                      </Tooltip>
                    ))}

                    {/* Si pas de participants inscrits pour cette session */}
                    <Dialog
                      open={open}
                      onClose={handleClose}
                      fullWidth
                      maxWidth="md"
                      PaperProps={{
                        sx: {
                          borderRadius: '28px',
                          bgcolor: '#F4F7FE',
                          boxShadow:
                            '0px 40px 58px -20px rgba(112, 144, 176, 0.12)',
                        },
                      }}
                    >
                      <DialogTitle sx={{ p: 3, pb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Box>
                            <Typography
                              variant="h4"
                              fontWeight="800"
                              color="#1B2559"
                              sx={{ letterSpacing: '-1px' }}
                            >
                              Espace Session
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#A3AED0', fontWeight: '600' }}
                            >
                              ID #{selectedClient?.id_wsp} •{' '}
                              {isIntervenant
                                ? 'Mode Intervenant'
                                : 'Mode Client'}
                            </Typography>
                          </Box>
                          <IconButton
                            onClick={handleClose}
                            sx={{ bgcolor: '#FFF' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </DialogTitle>

                      <DialogContent sx={{ px: 3, pb: 3 }}>
                        {selectedContext && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 3,
                            }}
                          >
                            {isIntervenant ? (
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2.5,
                                  borderRadius: '24px',
                                  background:
                                    'linear-gradient(135deg, #4318FF 0%, #3311CC 100%)',
                                  color: '#FFF',
                                  boxShadow:
                                    '0px 20px 40px rgba(67, 24, 255, 0.25)',
                                }}
                              >
                                <Grid container alignItems="center" spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                                          width: 50,
                                          height: 50,
                                          fontWeight: '800',
                                          border:
                                            '2px solid rgba(255, 255, 255, 0.4)',
                                        }}
                                      >
                                        {selectedContext?.participant?.client?.prenom_client?.charAt(
                                          0
                                        )}
                                      </Avatar>
                                      <Box>
                                        <Typography
                                          variant="h5"
                                          fontWeight="700"
                                        >
                                          {
                                            selectedContext?.participant?.client
                                              ?.prenom_client
                                          }
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            opacity: 0.8,
                                            fontWeight: '600',
                                          }}
                                        >
                                          Client à accompagner
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      justifyContent={{ md: 'flex-end' }}
                                    >
                                      {/* Bouton Copier */}
                                      <Button
                                        variant="contained"
                                        onClick={() => {
                                          navigator.clipboard.writeText(
                                            selectedContext?.participant?.client
                                              ?.meeting_link_wsp
                                          );
                                          // Optionnel: Ajouter une notification de succès ici
                                        }}
                                        sx={{
                                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                                          backdropFilter: 'blur(10px)',
                                          color: '#FFF',
                                          borderRadius: '12px',
                                          textTransform: 'none',
                                          fontWeight: '600',
                                          '&:hover': {
                                            bgcolor:
                                              'rgba(255, 255, 255, 0.25)',
                                          },
                                        }}
                                        startIcon={<ContentCopyIcon />}
                                      >
                                        Copier le lien
                                      </Button>

                                      {/* Bouton Rejoindre */}
                                      <Button
                                        variant="contained"
                                        href={
                                          selectedContext?.participant?.client
                                            ?.meeting_link_wsp
                                        }
                                        target="_blank"
                                        sx={{
                                          bgcolor: '#FFF',
                                          color: '#4318FF',
                                          borderRadius: '12px',
                                          px: 3,
                                          textTransform: 'none',
                                          fontWeight: '800',
                                          '&:hover': { bgcolor: '#F4F7FE' },
                                        }}
                                        startIcon={<VideoIcon />}
                                      >
                                        Lancer Zoom
                                      </Button>
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </Paper>
                            ) : (
                              /* ============================================================
             VUE CLIENT : INFOS COMPLÈTES (Si besoin de switcher)
             ============================================================ */
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 2.5,
                                  borderRadius: '24px',
                                  bgcolor: '#FFF',
                                  border: '1px solid #E2E8F0',
                                }}
                              >
                                <Grid container alignItems="center" spacing={3}>
                                  <Grid item xs={12} md={7}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2.5,
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          bgcolor: '#4318FF',
                                          width: 60,
                                          height: 60,
                                          fontSize: '1.2rem',
                                          fontWeight: '800',
                                          boxShadow:
                                            '0px 10px 20px rgba(67, 24, 255, 0.15)',
                                        }}
                                      >
                                        {selectedContext?.participant?.client?.prenom_client?.charAt(
                                          0
                                        )}
                                      </Avatar>
                                      <Box>
                                        <Typography
                                          variant="h6"
                                          fontWeight="800"
                                          color="#1B2559"
                                        >
                                          {
                                            selectedContext?.participant?.client
                                              ?.prenom_client
                                          }{' '}
                                          {
                                            selectedContext?.participant?.client
                                              ?.nom_client
                                          }
                                        </Typography>

                                        <Stack
                                          direction={{
                                            xs: 'column',
                                            sm: 'row',
                                          }}
                                          spacing={{ xs: 0.5, sm: 2 }}
                                          sx={{ mt: 0.5 }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.5,
                                            }}
                                          >
                                            <EmailIcon
                                              sx={{
                                                fontSize: 16,
                                                color: '#A3AED0',
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              fontWeight="600"
                                              color="#707EAE"
                                            >
                                              {
                                                selectedContext?.participant
                                                  ?.client?.email_client
                                              }
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.5,
                                            }}
                                          >
                                            <PhoneIcon
                                              sx={{
                                                fontSize: 16,
                                                color: '#A3AED0',
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              fontWeight="600"
                                              color="#707EAE"
                                            >
                                              {selectedContext?.participant
                                                ?.client?.phone_client ||
                                                'Non renseigné'}
                                            </Typography>
                                          </Box>
                                        </Stack>

                                        {selectedContext?.participant?.client
                                          ?.adresse_client && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.5,
                                              mt: 0.5,
                                            }}
                                          >
                                            <LocationIcon
                                              sx={{
                                                fontSize: 16,
                                                color: '#A3AED0',
                                              }}
                                            />
                                            <Typography
                                              variant="caption"
                                              fontWeight="600"
                                              color="#707EAE"
                                            >
                                              {
                                                selectedContext?.participant
                                                  ?.client?.adresse_client
                                              }
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </Grid>

                                  <Grid item xs={12} md={5}>
                                    <Stack
                                      direction="row"
                                      spacing={2}
                                      justifyContent={{ md: 'flex-end' }}
                                    >
                                      <Chip
                                        label={
                                          selectedContext?.participant?.client
                                            ?.etat_abn === 1
                                            ? 'Compte Actif'
                                            : 'En attente'
                                        }
                                        sx={{
                                          bgcolor:
                                            selectedContext?.participant?.client
                                              ?.etat_abn === 1
                                              ? '#E6FAF5'
                                              : '#FFF4E5',
                                          color:
                                            selectedContext?.participant?.client
                                              ?.etat_abn === 1
                                              ? '#01B574'
                                              : '#FF9933',
                                          fontWeight: '700',
                                          borderRadius: '10px',
                                          fontSize: '0.75rem',
                                        }}
                                      />
                                      <Button
                                        variant="contained"
                                        href={
                                          selectedContext?.participant?.client
                                            ?.meeting_link_wsp
                                        }
                                        target="_blank"
                                        startIcon={<VideoIcon />}
                                        sx={{
                                          bgcolor: '#4318FF',
                                          color: '#FFF',
                                          borderRadius: '14px',
                                          px: 3,
                                          textTransform: 'none',
                                          fontWeight: '700',
                                          '&:hover': { bgcolor: '#3311CC' },
                                          boxShadow:
                                            '0px 10px 20px rgba(67, 24, 255, 0.2)',
                                        }}
                                      >
                                        Rejoindre l'appel
                                      </Button>
                                    </Stack>
                                  </Grid>
                                </Grid>{' '}
                              </Paper>
                            )}

                            {/* SECTION CHAT : LARGEUR TOTALE DANS LES DEUX CAS */}
                            <Box
                              sx={{
                                width: '100%',
                                bgcolor: '#FFF',
                                borderRadius: '24px',
                                border: '1px solid #E2E8F0',
                                minHeight: '400px',
                                overflow: 'hidden',
                              }}
                            >
                              <ChatAtelier
                                session={selectedContext?.session}
                                participant={selectedContext?.participant}
                              />
                            </Box>
                          </Box>
                        )}
                      </DialogContent>
                    </Dialog>

                    <style>
                      {`
  @keyframes pulse {
    0% { transform: scale(0.95); opacity: 0.7; }
    70% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(0.95); opacity: 0.7; }
  }
`}
                    </style>
                  </div>
                </div>
              );
            })}
          </div>

          {/* COLONNE DROITE */}
          <div className="col-side">
            <div
              className="card-white"
              style={{
                padding: 0,
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
              }}
            >
              <img
                src={`${process.env.REACT_APP_API_URL}/storage/workshop/${workshop.photo_ws}`}
                alt="Aperçu"
                style={{ width: '100%', height: '300px', objectFit: 'cover' }}
              />
            </div>

            <div className="card-white" style={{ textAlign: 'center' }}>
              <Typography
                variant="overline"
                sx={{ color: '#A3AED0', fontWeight: '900' }}
              >
                Formateur Responsable
              </Typography>
              <Avatar
                src={`${process.env.REACT_APP_API_URL}/storage/workshop/${workshop.intervenant?.photo_user}`}
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  my: 2,
                  border: '5px solid #F4F7FE',
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                {workshop.intervenant?.prenom_user}{' '}
                {workshop.intervenant?.nom_user}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#4318FF', fontWeight: '700', mb: 3 }}
              >
                {workshop.intervenant?.title_user}
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: '15px',
                  textTransform: 'none',
                  fontWeight: '700',
                }}
              >
                Voir le profil expert
              </Button>
            </div>

            <div className="card-white">
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Résumé financier
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    Inscriptions totales
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: '800' }}>
                    {allParticipantsCount}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    Chiffre d'affaires estimé
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: '800', color: '#05CD99' }}
                  >
                    {allParticipantsCount * workshop.price_ws} €
                  </Typography>
                </Box>
              </Stack>
            </div>
          </div>
        </div>
      </div>

      <AddParticipantModal
        open={openAddModal}
        handleClose={() => setOpenAddModal(false)}
        workshop={workshop}
        refreshData={fetchDetails}
      />
    </div>
  );
};

const FeatureItem = ({ icon, text }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{ p: 1.5, bgcolor: '#F8F9FE', borderRadius: '12px' }}
  >
    {icon}
    <Typography variant="body2" sx={{ fontWeight: '800', color: '#1B254B' }}>
      {text}
    </Typography>
  </Stack>
);

export default WorkshopDetails;
